import { useCallback, useMemo, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { isValidPhoneNumber } from 'react-phone-number-input/min';
import { twMerge } from 'tailwind-merge';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DialogProps, Heading, Text } from 'react-aria-components';
import {
  CourseDialogBody,
  CourseDialogFooter,
} from 'src/components/ui/CourseInfoDialog';
import Button from 'src/components/ui/Button';
import FormTextField from 'src/components/form/FormTextField';
import FormPhoneField from 'src/components/form/FormPhoneField';
import FormSelect from 'src/components/form/FormSelect';
import BlockTitle from 'src/components/ui/BlockTitle';
import Dialog from 'src/components/ui/Dialog';
import LinkButton from 'src/components/ui/LinkButton';
import FormMultiSelect from 'src/components/form/FormMultiSelect';
import InstitutionField from 'src/components/auth/InstitutionField';
import { focusFirstProblem } from 'src/utils/focus-first-problem';
import CheckIcon from 'src/assets/icons/shield-check.svg';
import { ENV_MODE, isWhiteLabel } from 'src/utils';
import { graphQLErrorNotification } from 'src/utils/notifications';
import CREATOR_ONBOARDING_REQUEST_SUBMIT from 'src/graphql/mutation/creator-onboarding-request-submit.graphql';
import INSTITUTION_LIST from 'src/graphql/query/institution-list.graphql';
import INSTITUTION_PROGRAMS from 'src/graphql/query/institution-programs.graphql';

import type { Institution, Program } from 'src/types/institution';
import type { Size } from 'src/types/common';

interface FeedbackDialogProps extends Omit<DialogProps, 'children'> {
  className?: string;
  size?: Size;
  /** The address typed on the sign-in page, to prefill Email. */
  defaultEmail?: string;
  onClose?: () => void;
}

const ROLES = [
  {
    id: 'PROFESSOR',
    name: 'Faculty',
    description: 'I teach courses and order course packs for them.',
  },
  {
    id: 'REPRESENTATIVE',
    name: 'Bookstore Manager',
    description: 'I order course packs for one or more campus bookstores.',
  },
  {
    id: 'ORGANIZATION_ADMIN',
    name: 'Organization Admin',
    description:
      'I manage course pack orders for every store in my organization.',
  },
  {
    id: 'INSTITUTION_ADMIN',
    name: 'Institution Admin',
    description: 'I manage course pack orders across my college or university.',
  },
];

const OTHER_PLACEHOLDER = "If yours isn't listed, enter its name";

// Server field keys → the form field that shows the error.
const SERVER_FIELDS: Record<string, keyof FeedbackSchemaType> = {
  email: 'email',
  first_name: 'firstName',
  last_name: 'lastName',
  phone: 'phone',
  organization_name: 'organizationName',
  bookstore_name: 'bookstoreName',
  message: 'message',
};

// Fields in the order they appear, so the first error is the topmost one.
const FIELD_ORDER: (keyof FeedbackSchemaType)[] = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'creatorType',
  'organizationName',
  'bookstoreName',
  'institutions',
  'institutionId',
  'storeInstitutionId',
  'institutionName',
  'programName',
  'message',
];

// One Program select per institution sits just above Other Program.
const fieldRank = (name: string) =>
  name.startsWith('programs.')
    ? FIELD_ORDER.indexOf('programName') - 0.5
    : FIELD_ORDER.indexOf(name as keyof FeedbackSchemaType);

// The fields area scrolls, so an error can be out of view.
const revealFirstError = (fields: string[]) =>
  focusFirstProblem(
    fields
      .filter((name) => fieldRank(name) >= 0)
      .sort((a, b) => fieldRank(a) - fieldRank(b))
  );

const serverFieldErrors = (error: ApolloError) =>
  Object.entries(error?.graphQLErrors[0]?.extensions?.fields ?? {}).map(
    ([key, value]) => ({ key, message: [value].flat().join(' ') })
  );

// RHF reads a numeric path segment as an array index, so the id is prefixed.
const programsKey = (institutionId: string) => `institution${institutionId}`;

const FeedbackDialog = ({
  size = 'medium',
  className,
  defaultEmail = '',
  onClose,
  ...props
}: FeedbackDialogProps) => {
  const isSmall = size === 'small';
  const [submittedEmail, setSubmittedEmail] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const {
    handleSubmit,
    clearErrors,
    control,
    watch,
    setError,
    formState,
    ...methods
  } = useForm<FeedbackSchemaType>({
    resolver: zodResolver(FeedbackSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: defaultEmail,
      phone: '',
      creatorType: '',
      institutions: [],
      institutionId: '',
      storeInstitutionId: '',
      institutionName: '',
      programs: {},
      programName: '',
      organizationName: '',
      bookstoreName: '',
      message: '',
    },
  });

  // Covers the reCAPTCHA token request as well as the mutation.
  const { isSubmitting } = formState;

  const creatorType = watch('creatorType');
  const institutions = watch('institutions');
  const institutionId = watch('institutionId');
  const storeInstitutionId = watch('storeInstitutionId');

  const isFaculty = creatorType === 'PROFESSOR';
  const isBookstoreManager = creatorType === 'REPRESENTATIVE';
  const isInstitutionAdmin = creatorType === 'INSTITUTION_ADMIN';
  const hasOrganization =
    isBookstoreManager || creatorType === 'ORGANIZATION_ADMIN';
  const hasPrograms = isFaculty || isInstitutionAdmin;

  // Each role keeps its own institution pick, so one made under another role
  // is neither shown nor sent. An organization admin has none.
  const institutionField = (
    {
      PROFESSOR: 'institutions',
      INSTITUTION_ADMIN: 'institutionId',
      REPRESENTATIVE: 'storeInstitutionId',
    } as const
  )[creatorType as string];

  const programInstitutions = isFaculty
    ? institutions
    : [{ value: institutionId, label: '' }].filter((i) => i.value);

  const [submitRequest, { error }] = useMutation(
    CREATOR_ONBOARDING_REQUEST_SUBMIT,
    {
      // Errors are shown in the form, so the global error toast in errorLink
      // is switched off for this mutation.
      context: { notifyOnError: false },
      onCompleted: (_, clientOptions) => {
        setSubmittedEmail(clientOptions?.variables?.input?.email);
      },
      onError: (apolloError) => {
        const fieldsFor = (key: string): FieldPath<FeedbackSchemaType>[] => {
          if (key === 'institution_ids')
            return institutionField ? [institutionField] : [];
          // The server does not say which institution's programs it means.
          if (key === 'program_ids')
            return programInstitutions.map(
              (i) => `programs.${programsKey(i.value)}` as const
            );

          return SERVER_FIELDS[key] ? [SERVER_FIELDS[key]] : [];
        };

        const fields = serverFieldErrors(apolloError).flatMap(
          ({ key, message }) => {
            const names = fieldsFor(key);

            names.forEach((name) => setError(name, { message }));
            return names;
          }
        );

        if (fields.length) revealFirstError(fields);
        if (isRecaptchaError(apolloError))
          graphQLErrorNotification(apolloError);
      },
    }
  );

  const onSubmit = useCallback(
    async (data: FeedbackSchemaType) => {
      clearErrors();

      const recaptcha = executeRecaptcha
        ? await executeRecaptcha('request_access')
        : null;

      const institutionIds =
        institutionField === 'institutions'
          ? data.institutions.map((i) => i.value)
          : [institutionField ? data[institutionField] : ''].filter(Boolean);

      const programIds = hasPrograms
        ? institutionIds.flatMap((id) =>
            (data.programs[programsKey(id)] ?? []).map((p) => p.value)
          )
        : [];

      await submitRequest({
        variables: {
          input: {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone || null,
            creatorType: data.creatorType,
            organizationName: hasOrganization ? data.organizationName : null,
            bookstoreName: isBookstoreManager ? data.bookstoreName : null,
            institutionIds,
            institutionName: (hasPrograms && data.institutionName) || null,
            programIds,
            programName: (hasPrograms && data.programName) || null,
            message: data.message || null,
            recaptcha,
          },
          whitelabelKey: isWhiteLabel ? ENV_MODE : null,
        },
      });
    },
    [
      submitRequest,
      clearErrors,
      executeRecaptcha,
      institutionField,
      isBookstoreManager,
      hasOrganization,
      hasPrograms,
    ]
  );

  return (
    <Dialog
      className={twMerge(
        'p-0 overflow-hidden max-h-[calc(var(--visual-viewport-height)*0.8)]',
        className
      )}
      {...props}
    >
      {({ close }) => {
        if (submittedEmail)
          return (
            <SuccessScreen
              email={submittedEmail}
              onClose={() => {
                close();
                if (onClose) onClose();
              }}
            />
          );

        return (
          <FormProvider
            watch={watch}
            control={control}
            clearErrors={clearErrors}
            handleSubmit={handleSubmit}
            setError={setError}
            formState={formState}
            {...methods}
          >
            <form
              onSubmit={handleSubmit(onSubmit, (errors) =>
                revealFirstError(Object.keys(errors))
              )}
              className="flex flex-col max-h-[inherit]"
            >
              <div className="flex-1 min-h-0 overflow-auto">
                <CourseDialogBody className="text-surface-90">
                  <Heading slot="title" className="text-2xl font-bold">
                    Request Access to Creator Studio
                  </Heading>
                  <Text
                    elementType="p"
                    className="text-sm text-surface-alt-75 font-normal mt-1 mb-2"
                  >
                    Creator Studio accounts are approved by the LAD team. Tell
                    us who you are and we&apos;ll email you once your request is
                    reviewed.
                  </Text>

                  <BlockTitle variant="v2">About You</BlockTitle>

                  <div className="grid grid-cols-2 gap-x-2.5 gap-y-4">
                    <FormTextField
                      name="firstName"
                      label="First Name"
                      // The user opened this dialog to request access; start at the first required field.
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                      isRequired
                      autoComplete="given-name"
                    />
                    <FormTextField
                      name="lastName"
                      label="Last Name"
                      isRequired
                      autoComplete="family-name"
                    />
                    <FormTextField
                      name="email"
                      label="Email"
                      placeholder="name@university.edu"
                      isRequired
                      autoComplete="email"
                    />
                    <FormPhoneField name="phone" label="Phone" />

                    <FormSelect
                      name="creatorType"
                      label="Your Role"
                      placeholder="Select your role"
                      items={ROLES}
                      className="col-span-full"
                      isRequired
                    />

                    {hasOrganization && (
                      <FormTextField
                        name="organizationName"
                        label="Organization"
                        description="The company that runs your bookstore."
                        isRequired
                      />
                    )}
                    {isBookstoreManager && (
                      <FormTextField
                        name="bookstoreName"
                        label="Bookstore"
                        description="The store you manage, with its campus."
                        isRequired
                      />
                    )}

                    {isFaculty && <InstitutionsMultiSelect />}
                    {isInstitutionAdmin && (
                      <InstitutionField
                        name="institutionId"
                        label="Institution"
                        placeholder="Select the institution you manage"
                        className="col-span-full"
                        isRequired
                      />
                    )}
                    {isBookstoreManager && (
                      <div className="col-span-full flex flex-col gap-1">
                        <InstitutionField
                          name="storeInstitutionId"
                          label="Institution"
                          placeholder="Select the school your store serves"
                        />
                        {/* Optional here, and the select cannot be emptied. */}
                        {storeInstitutionId && (
                          <LinkButton
                            hasArrow={false}
                            className="self-end text-xs"
                            onPress={() =>
                              methods.setValue('storeInstitutionId', '')
                            }
                          >
                            Clear Institution
                          </LinkButton>
                        )}
                      </div>
                    )}
                    {hasPrograms && (
                      <FormTextField
                        name="institutionName"
                        label="Other Institution"
                        placeholder={OTHER_PLACEHOLDER}
                        className="col-span-full"
                      />
                    )}

                    {hasPrograms &&
                      programInstitutions.map((institution) => (
                        <ProgramsMultiSelect
                          key={institution.value}
                          institutionId={institution.value}
                          // Named only when there are several to tell apart.
                          institutionName={
                            programInstitutions.length > 1
                              ? institution.label
                              : ''
                          }
                        />
                      ))}
                    {hasPrograms && (
                      <FormTextField
                        name="programName"
                        label="Other Program"
                        placeholder={OTHER_PLACEHOLDER}
                        className="col-span-full"
                      />
                    )}

                    <FormTextField
                      name="message"
                      label="Message"
                      placeholder="Courses you teach, the store you manage, or your LAD contact."
                      rows={4}
                      className="col-span-full"
                    />
                  </div>
                </CourseDialogBody>
              </div>

              <CourseDialogFooter isSmall={isSmall}>
                {error && <ErrorText error={error} />}
                <Button
                  className="justify-center"
                  variant="surface"
                  onPress={close}
                  ghost
                >
                  Cancel
                </Button>
                <Button
                  className="justify-center"
                  type="submit"
                  variant="success"
                  isDisabled={isSubmitting}
                  loading={isSubmitting}
                >
                  Send Request
                </Button>
              </CourseDialogFooter>
            </form>
          </FormProvider>
        );
      }}
    </Dialog>
  );
};

const InstitutionsMultiSelect = () => {
  const { data, loading } = useQuery(INSTITUTION_LIST);
  const institutions = useMemo(() => {
    const list = data?.institutionsList ?? [];

    return list
      .map((institution: Institution) => ({
        value: institution.institutionId,
        label: institution.name,
      }))
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label)
      );
  }, [data]);

  return (
    <FormMultiSelect
      name="institutions"
      label="Institution"
      placeholder="Select one or more"
      options={institutions}
      isRequired
      isDisabled={loading}
      className="col-span-full"
      maxMenuHeight={180}
    />
  );
};

const ProgramsMultiSelect = ({
  institutionId,
  institutionName,
}: {
  institutionId: string;
  institutionName: string;
}) => {
  const { data, loading } = useQuery(INSTITUTION_PROGRAMS, {
    variables: { id: institutionId },
  });
  const programs = useMemo(
    () =>
      (data?.institution?.programs ?? []).map((program: Program) => ({
        value: program.programId,
        label: program.name,
      })),
    [data]
  );

  return (
    <FormMultiSelect
      name={`programs.${programsKey(institutionId)}`}
      label={institutionName ? `Program at ${institutionName}` : 'Program'}
      placeholder={
        loading || programs.length
          ? 'Select your program'
          : 'No available programs'
      }
      options={programs}
      isDisabled={loading || !programs.length}
      className="col-span-full"
      maxMenuHeight={180}
    />
  );
};

const SuccessScreen = ({
  email,
  onClose,
}: {
  email: string;
  onClose: () => void;
}) => (
  <div className="px-4 pt-8 pb-4 text-center text-base font-medium text-surface-alt-90">
    <CheckIcon className="inline-block w-20 h-20" />
    <Heading level={5} className="text-2xl font-bold mt-9 mb-4">
      Request Received
    </Heading>
    <Text elementType="p">
      Our team reviews every request by hand. We&apos;ll email{' '}
      <strong>{email}</strong> once yours is reviewed.
    </Text>
    <Text
      elementType="p"
      className="text-sm text-surface-alt-75 font-normal mt-3"
    >
      No email from us? Check your spam folder, or write to{' '}
      <a
        className="text-surface-alt-90 underline"
        href="mailto:support@lad.pub"
      >
        support@lad.pub
      </a>
      .
    </Text>

    <Button className="mt-4" onPress={onClose} variant="secondary">
      Back to Sign In
    </Button>
  </div>
);

const isRecaptchaError = (error: ApolloError) =>
  serverFieldErrors(error).some(({ key }) => key === 'recaptcha');

// Shown only when nothing else explains the failure: field errors sit under
// their inputs and a reCAPTCHA failure gets the support toast.
const ErrorText = ({ error }: { error: ApolloError }) => {
  const explained = serverFieldErrors(error).some(
    ({ key }) =>
      SERVER_FIELDS[key] ||
      ['institution_ids', 'program_ids', 'recaptcha'].includes(key)
  );

  if (explained) return null;

  return (
    <Text role="alert" className="text-xs text-red font-medium">
      We couldn&apos;t send your request. Try again, or write to
      support@lad.pub.
    </Text>
  );
};

const OptionSchema = z.object({
  label: z.string(),
  value: z.string(),
});

const FeedbackSchema = z
  .object({
    firstName: z.string().min(1, 'Enter your first name.'),
    lastName: z.string().min(1, 'Enter your last name.'),
    email: z
      .string()
      .min(1, 'Enter your email address.')
      .email('Enter a valid email address, like name@university.edu.'),
    phone: z.string(),
    // Empty until the applicant picks a role.
    creatorType: z.enum([
      '',
      'PROFESSOR',
      'REPRESENTATIVE',
      'ORGANIZATION_ADMIN',
      'INSTITUTION_ADMIN',
    ]),
    organizationName: z.string(),
    bookstoreName: z.string(),
    institutions: z.array(OptionSchema),
    institutionId: z.string(),
    storeInstitutionId: z.string(),
    institutionName: z.string(),
    // A programs select that was shown but left empty registers `undefined`.
    programs: z.record(z.array(OptionSchema).optional()),
    programName: z.string(),
    message: z.string().max(4000, 'Keep your message under 4,000 characters.'),
  })
  .superRefine((data, ctx) => {
    const required = (path: keyof typeof data, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });

    const { creatorType } = data;
    const isBookstoreManager = creatorType === 'REPRESENTATIVE';

    if (!creatorType) required('creatorType', 'Select your role.');

    if (data.phone.length && !isValidPhoneNumber(data.phone)) {
      required('phone', 'Enter a valid phone number, or leave it blank.');
    }

    if (
      (isBookstoreManager || creatorType === 'ORGANIZATION_ADMIN') &&
      !data.organizationName
    ) {
      required('organizationName', 'Enter your organization.');
    }

    if (isBookstoreManager && !data.bookstoreName) {
      required('bookstoreName', 'Enter your bookstore.');
    }

    if (
      creatorType === 'PROFESSOR' &&
      !data.institutions.length &&
      !data.institutionName
    ) {
      required(
        'institutions',
        'Select your institution, or enter it under Other Institution.'
      );
    }

    if (
      creatorType === 'INSTITUTION_ADMIN' &&
      !data.institutionId &&
      !data.institutionName
    ) {
      required(
        'institutionId',
        'Select the institution you manage, or enter it under Other Institution.'
      );
    }

    return ctx;
  });

type FeedbackSchemaType = z.infer<typeof FeedbackSchema>;

export default FeedbackDialog;
